// src/state/GameState.ts
//
// Phase 4 — single source of truth for the run. Scenes read from it; mutations
// only via reducer methods. RunState graph is encapsulated; deck objects are
// kept in sync via syncDecks() after every mutation.

import type {
  Fiche,
  Ball,
  Relic,
  Consumable,
  BoardCell,
  SlotNumber,
  RunState,
  SpinResult,
  SpinResult16,
} from './types';
import type { Rng } from '../systems/rng';
import { createRng, pickFromArray } from '../systems/rng';
import { CONSUMABLES, consumableFromDef } from '../systems/consumables';
import type { ConsumableDef } from './types';
import { composeWheel } from '../systems/wheel';
import { buildCanonicalNumbersDeck } from '../systems/numbersDeck';
import { buildCanonicalBoardLayout } from '../systems/board';
import { FICHES_BY_ID } from '../content/fiches';
import { BALLS_BY_ID } from '../content/balls';
import { createDeck, drawOne, drawN, discard, reshuffleAll, type Deck } from '../systems/deck';

export interface GameStateOptions {
  seed?: string;
}

export class GameState {
  private state: RunState;
  private rng: Rng;
  private ficheDeckObj: Deck<Fiche>;
  private ballDeckObj: Deck<Ball>;

  constructor(opts: GameStateOptions = {}) {
    const seed = opts.seed ?? defaultSeed();
    this.rng = createRng(seed);

    const ficheDeck = buildStartingFicheDeck();
    const ballDeck = buildStartingBallDeck();

    this.ficheDeckObj = createDeck(ficheDeck, this.rng);
    this.ballDeckObj = createDeck(ballDeck, this.rng);

    // Draw the FIRST ball so player sees nextBall before placing fiches
    const firstBall = drawOne(this.ballDeckObj, this.rng);

    // Phase 19 (19-01, CONSUMABLE-19): draw 2 starting consumables from the
    // full pool, without replacement. Consumes 2 rng() calls — the canonical-
    // seed regression canary in canonical-seed.test.ts is re-anchored to
    // account for these. Pattern mirrors RunStartScene candidate selection.
    const startingConsumables: Consumable[] = [];
    const consumablePool: ConsumableDef[] = [...CONSUMABLES];
    for (let i = 0; i < 2 && consumablePool.length > 0; i++) {
      const picked = pickFromArray(this.rng, consumablePool);
      consumablePool.splice(consumablePool.indexOf(picked), 1);
      startingConsumables.push(consumableFromDef(picked.defId));
    }

    // Phase 13 (WHEEL-06, WHEEL-08): canonical numbers deck + empty
    // voidPositions at run-start. Mutated by Phase 15 consumables; persists
    // across rounds within a run.
    const numbersDeck = buildCanonicalNumbersDeck();
    const voidPositions: number[] = [];
    // Phase 13 (WHEEL-08): wheel composed ONCE per run. Deterministic given
    // (numbersDeck, voidPositions, this.rng) — see PITFALLS Pitfall 2.
    const wheel = composeWheel(numbersDeck, voidPositions, this.rng);
    // Phase 13 (BOARD-01): canonical 2D layout. structuredClone so future
    // Phase 15 cell-mutator consumables don't accidentally mutate the
    // exported CANONICAL_BOARD_LAYOUT singleton via shared refs.
    const boardLayout = structuredClone(buildCanonicalBoardLayout());

    this.state = {
      seed,
      money: 4,
      ante: 1,
      roundIndex: 0,
      ficheDeck: this.ficheDeckObj.draw,
      ficheDiscard: this.ficheDeckObj.discard,
      ficheHand: [],
      ballDeck: this.ballDeckObj.draw,
      ballDiscard: this.ballDeckObj.discard,
      nextBall: firstBall,
      // 12b (BALL-09): second_ball relic slot. Independent of nextBall —
      // reverting multi-ball did not bring it back. Consumed before nextBall
      // when present (priority chain in RoundScene.onSpinTapped).
      queuedBall: null,
      relics: [],
      wheel, // ← composed at run-start via composeWheel (was [...WHEEL])
      numbersDeck, // ← NEW (Phase 13 WHEEL-06)
      voidPositions, // ← NEW (Phase 13 WHEEL-07)
      boardLayout, // ← NEW (Phase 13 BOARD-01)
      history: [],
      // Phase 15 (CONSUMABLE-01): consumable inventory + derived state fields.
      // Phase 19 (19-01): initialized with 2 starting consumables drawn above.
      consumables: startingConsumables,
      forcedNumber: null,
      pendingInterestBonus: 0,
      // Phase 16 (ROUND-07, FICHE-13, SHOP-08)
      lastSpin: null,
      currentStreak: 0,
      standardFichePurchasesThisVisit: 0,
      roundFicheScores: {},
      // Phase 23: tetris fiches + pawn promotion init.
      pawnProgress: {},
      tetrisCompletedRows: [],
    };
  }

  // ------------------- accessors -------------------

  getState(): Readonly<RunState> {
    return this.state;
  }
  getRng(): Rng {
    return this.rng;
  }

  // ------------------- reducers (the only mutation surface) -------------------

  /** Draw N fiches from deck into hand. */
  drawFiches(n: number): Fiche[] {
    const drawn = drawN(this.ficheDeckObj, n, this.rng);
    this.state.ficheHand = drawn;
    this.syncDecks();
    return drawn;
  }

  /** Discard the entire current hand (e.g. after spin). */
  discardHand(): void {
    discard(this.ficheDeckObj, this.state.ficheHand);
    this.state.ficheHand = [];
    this.syncDecks();
  }

  /** Discard the current ball, draw the next, return it (or null if deck empty). */
  cycleBall(): Ball | null {
    if (this.state.nextBall) {
      discard(this.ballDeckObj, [this.state.nextBall]);
    }
    const next = drawOne(this.ballDeckObj, this.rng);
    this.state.nextBall = next;
    this.syncDecks();
    return next;
  }

  /** Reshuffle the ball deck (all cards back into draw) — call between rounds. */
  reshuffleBalls(): void {
    if (this.state.nextBall) {
      // Put the held-but-unused next-ball back so reshuffle covers it
      discard(this.ballDeckObj, [this.state.nextBall]);
      this.state.nextBall = null;
    }
    reshuffleAll(this.ballDeckObj, this.rng);
    // Then draw a fresh "next" for the new round
    this.state.nextBall = drawOne(this.ballDeckObj, this.rng);
    this.syncDecks();
  }

  /** Add money (positive or negative). Clamps at 0. */
  adjustMoney(delta: number): void {
    this.state.money = Math.max(0, this.state.money + delta);
  }

  /** Add a relic to the slot list. Silently rejects 4th and beyond (≤3 cap). */
  addRelic(relic: Relic): void {
    if (this.state.relics.length >= 3) return; // hard cap, silently rejects
    this.state.relics.push(relic);
  }

  /** Add a fiche to the player's deck (drops onto the draw pile). */
  addFicheToDeck(fiche: Fiche): void {
    this.ficheDeckObj.draw.push(fiche);
    this.syncDecks();
  }

  /** Add a ball to the player's deck (drops onto the draw pile). */
  addBallToDeck(ball: Ball): void {
    this.ballDeckObj.draw.push(ball);
    this.syncDecks();
  }

  /**
   * 12b: Replace nextBall with newBall. Used by the shop replace-ball flow
   * (plan 12-05) when the player buys a different ball type and the
   * second_ball relic is NOT held (or queuedBall is already filled). Caller
   * decides slot logic — this reducer always replaces nextBall.
   */
  replaceBall(newBall: Ball): void {
    this.state.nextBall = newBall;
  }

  /**
   * 12b: Fill the queuedBall slot. Used by the shop replace-ball flow when
   * the player has the second_ball relic and queuedBall is currently null.
   */
  setQueuedBall(ball: Ball): void {
    this.state.queuedBall = ball;
  }

  /**
   * 12b: Clear the queuedBall slot. Called from RoundScene after a spin
   * where the queued ball was the active one (priority chain:
   * `queuedBall ?? nextBall`).
   *
   * IMPORTANT (clarified per 12-REVIEW ME-02): this reducer does NOT advance
   * `nextBall`. The caller is responsible for cycling `nextBall` on
   * subsequent spins via `cycleBall()`. After a queuedBall consumption,
   * `nextBall` stays unchanged — it becomes the primary ball on the next
   * spin and only cycles on the spin AFTER that. So a queued ball
   * effectively "skips" one cycleBall step compared to v1's ball-cycle
   * pace.
   *
   * Today the only filler of `queuedBall` is the shop replace-ball flow,
   * which runs between rounds — so over a 3-spin round the sequence is
   * `queued, next, cycled-next` and behaves correctly. If a future patch
   * fills `queuedBall` mid-round (e.g. a `loaded_ball` consumable in
   * Phase 15), keep this contract in mind.
   */
  consumeQueuedBall(): void {
    this.state.queuedBall = null;
  }

  // ── Phase 15 (CONSUMABLE-01): consumable reducers ───────────────────────

  /** Add a consumable. Silently rejects if already at cap of 3. */
  addConsumable(consumable: Consumable): void {
    if (this.state.consumables.length >= 3) return;
    this.state.consumables.push(consumable);
  }

  /** Remove a consumable by instance id. No-op if not found. */
  removeConsumable(id: string): void {
    const idx = this.state.consumables.findIndex((c) => c.id === id);
    if (idx >= 0) this.state.consumables.splice(idx, 1);
  }

  /**
   * Replace a board cell by its cells[] index. Deep-clones the replacement
   * so the caller's object is not shared with RunState. Also updates the grid
   * reference at [mutatedCell.row][mutatedCell.col].
   */
  mutateBoardCell(cellIdx: number, mutatedCell: BoardCell): void {
    const layout = this.state.boardLayout;
    const oldCell = layout.cells[cellIdx];
    const oldRow = oldCell?.row;
    const oldCol = oldCell?.col;
    const cloned = structuredClone(mutatedCell);
    layout.cells[cellIdx] = cloned;
    // Update all grid positions whose stored cell has the same (row, col) as the old cell.
    // Position-keyed matching (not object identity) handles the zero aliases even after
    // a JSON round-trip, where grid[][] and cells[] hold separate objects with equal values.
    for (let r = 0; r < layout.grid.length; r++) {
      const row = layout.grid[r];
      if (!row) continue;
      for (let c = 0; c < row.length; c++) {
        const entry = row[c];
        if (entry && entry.row === oldRow && entry.col === oldCol) row[c] = cloned;
      }
    }
  }

  /**
   * Remove a board cell by its cells[] index. Nulls out the grid reference.
   * Called by the merge consumable (plan 15-03) after boosting cellA's payoutMult.
   * No-op if cellIdx is out of bounds.
   */
  removeBoardCell(cellIdx: number): void {
    const layout = this.state.boardLayout;
    const cell = layout.cells[cellIdx];
    if (!cell) return;
    layout.cells.splice(cellIdx, 1);
    if (layout.grid[cell.row]) {
      layout.grid[cell.row]![cell.col] = null;
    }
  }

  /**
   * Replace the numbers deck entirely. Callers must have already validated
   * floor/lock constraints (removeNumber returns ok:false otherwise).
   * Does NOT recompose the wheel — wheel recomposition from deck happens
   * at round start only (Phase 13 WHEEL-08).
   */
  mutateNumbersDeck(newDeck: SlotNumber[]): void {
    this.state.numbersDeck = [...newDeck];
  }

  /** Recompose the wheel from the current numbersDeck + voidPositions. Call after deck mutations (Mint, SkipSlot). */
  recomposeWheel(): void {
    this.state.wheel = composeWheel(this.state.numbersDeck, this.state.voidPositions, this.rng);
  }

  /** Set the forcedNumber for the next spin (loaded_ball consumable). */
  setForcedNumber(n: SlotNumber | null): void {
    this.state.forcedNumber = n;
  }

  /** Set the pending interest bonus (compound consumable). */
  setPendingInterestBonus(n: number): void {
    this.state.pendingInterestBonus = n;
  }

  // ── Phase 16 reducers ───────────────────────────────────────────────────

  /** Set the round-bounded spin snapshot for prev-spin fiche effects. */
  setLastSpin(spin: SpinResult16 | null): void {
    this.state.lastSpin = spin;
  }

  /** Clear lastSpin (convenience; called at round start). */
  clearLastSpin(): void {
    this.state.lastSpin = null;
  }

  /** Set the run-level hit streak counter. */
  setCurrentStreak(n: number): void {
    this.state.currentStreak = n;
  }

  /** Set the within-visit standard fiche purchase counter. */
  setStandardFichePurchasesThisVisit(n: number): void {
    this.state.standardFichePurchasesThisVisit = n;
  }

  /** Append a SpinResult to history. */
  recordSpin(result: SpinResult): void {
    this.state.history.push(result);
  }

  /**
   * Phase 16 (RELIC-08): Preview the next n fiches using Rng.clone().
   * Returns the fiches that the next drawFiches(n) call will produce,
   * WITHOUT advancing the real rng or mutating deck state.
   *
   * Contract: the caller must not consume any rng between calling
   * peekDrawFiches() and the corresponding drawFiches() call. If this
   * contract is honoured, the real draw will produce identical fiches.
   *
   * Usage: RoundScene.create() calls peekDrawFiches(n) to show the peek
   * overlay, then immediately calls drawFiches(n) for the actual hand.
   */
  peekDrawFiches(n: number): Fiche[] {
    const deckClone: Deck<Fiche> = {
      draw: [...this.ficheDeckObj.draw],
      discard: [...this.ficheDeckObj.discard],
    };
    const rngClone = this.rng.clone();
    return drawN(deckClone, n, rngClone);
  }

  /** Advance to the next round (small→big→boss; boss→shop→ante+1 small handled separately). */
  advanceRound(): void {
    if (this.state.roundIndex < 2) {
      this.state.roundIndex = (this.state.roundIndex + 1) as 0 | 1 | 2;
    } else {
      // After boss, advance ante
      if (this.state.ante < 3) {
        this.state.ante = (this.state.ante + 1) as 1 | 2 | 3;
        this.state.roundIndex = 0;
      }
      // If ante === 3 and roundIndex was 2, the run is "won" — caller transitions to WinScene.
    }
    // Reset round-scoped fiche scores so relics start fresh each round.
    this.state.roundFicheScores = {};
  }

  /** Accumulate per-spin fiche totals into the round-level score tracker. */
  updateRoundFicheScores(scores: ReadonlyArray<{ ficheId: string; total: number }>): void {
    for (const { ficheId, total } of scores) {
      this.state.roundFicheScores[ficheId] = (this.state.roundFicheScores[ficheId] ?? 0) + total;
    }
  }

  // ── Phase 23 reducers ──────────────────────────────────────────────────

  /**
   * Update pawn placement progress for a chess pawn fiche. Streak increments
   * when the new placement is in the same column AND exactly one row up
   * from the previous one; resets to 1 otherwise. Promotion at streak >= 4
   * with row === 0 (dozen-sub row) is decided by the caller (RoundScene).
   */
  updatePawnProgress(ficheId: string, col: number, row: number): void {
    const prev = this.state.pawnProgress[ficheId];
    const streak = prev && prev.col === col && row === prev.row - 1 ? prev.streak + 1 : 1;
    this.state.pawnProgress[ficheId] = { col, row, streak };
  }

  /**
   * Permanently mutate a fiche's effect. Searches hand → deck → discard for
   * the matching id and replaces the effect in place. Used by pawn promotion
   * to upgrade a pawn into a knight/bishop/rook/queen for the rest of the run.
   *
   * The arrays in state.* and ficheDeckObj.* share object references after
   * syncDecks(), so mutating the fiche object in-place is safe — both views
   * see the change without a re-sync. We still call syncDecks() defensively
   * to keep the state.* arrays pointing at the latest deckObj snapshots.
   */
  mutateFicheEffect(ficheId: string, newEffect: import('./types').FicheEffect): void {
    for (const arr of [this.state.ficheHand, this.state.ficheDeck, this.state.ficheDiscard]) {
      const f = (arr as import('./types').Fiche[]).find((x) => x.id === ficheId);
      if (f) {
        (f as { effect?: import('./types').FicheEffect }).effect = newEffect;
        break;
      }
    }
    this.syncDecks();
  }

  /**
   * Mark a board row as completed by tetris fiches. Each unique completed
   * row grants +1 permanent flatMultBonus for the rest of the run. Returns
   * true if the row was newly added (caller can show a toast); false if it
   * was already completed earlier this run.
   */
  completeTetrisRow(row: number): boolean {
    if (this.state.tetrisCompletedRows.includes(row)) return false;
    this.state.tetrisCompletedRows.push(row);
    return true;
  }

  /**
   * After drawFiches(), apply relic effects that guarantee certain fiches appear
   * in hand. Currently: `golden_touch` forces the top scorer, `reliable` forces
   * the second-top scorer. If the target fiche is not already in hand, it is
   * pulled from discard (or draw) and a non-forced hand fiche is sent to discard.
   * No-op if roundFicheScores is empty (first spin of round) or relics not held.
   */
  applyHandRelics(n: number): void {
    const relics = this.state.relics;
    const hasGolden = relics.some((r) => r.defId === 'golden_touch');
    const hasReliable = relics.some((r) => r.defId === 'reliable');
    if (!hasGolden && !hasReliable) return;

    const scores = this.state.roundFicheScores;
    const sorted = Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .map(([ficheId]) => ficheId);
    if (sorted.length === 0) return;

    const forced: string[] = [];
    if (hasGolden && sorted[0]) forced.push(sorted[0]);
    if (hasReliable && sorted[1]) forced.push(sorted[1]);
    const uniqueForced = [...new Set(forced)];

    for (const ficheId of uniqueForced) {
      if (this.state.ficheHand.some((f) => f.id === ficheId)) continue;

      // Pull from discard first, then draw pile
      let fiche: Fiche | undefined;
      const discardIdx = this.ficheDeckObj.discard.findIndex((f) => f.id === ficheId);
      if (discardIdx >= 0) {
        [fiche] = this.ficheDeckObj.discard.splice(discardIdx, 1);
      } else {
        const drawIdx = this.ficheDeckObj.draw.findIndex((f) => f.id === ficheId);
        if (drawIdx >= 0) {
          [fiche] = this.ficheDeckObj.draw.splice(drawIdx, 1);
        }
      }
      if (!fiche) continue;

      // If hand is at capacity, displace one non-forced fiche back to discard
      if (this.state.ficheHand.length >= n) {
        const nonForcedIdx = this.state.ficheHand.findIndex((f) => !uniqueForced.includes(f.id));
        if (nonForcedIdx >= 0) {
          const displaced = this.state.ficheHand.splice(nonForcedIdx, 1)[0]!;
          this.ficheDeckObj.discard.push(displaced);
        } else {
          this.ficheDeckObj.draw.push(fiche);
          continue;
        }
      }
      this.state.ficheHand.push(fiche);
    }
    this.syncDecks();
  }

  // ------------------- serialization -------------------

  toJSON(): SaveSnapshot {
    return {
      v: 13,
      state: structuredClone(this.state),
    };
  }

  static fromJSON(snap: SaveSnapshot): GameState {
    if (snap.v !== 13) throw new Error('Schema mismatch');
    const gs = Object.create(GameState.prototype) as GameState;
    // Re-create RNG from seed (lose internal RNG position — acceptable for v1; future runs from save replay deterministically from save point onward only with care)
    (gs as unknown as { rng: Rng }).rng = createRng(snap.state.seed);
    (gs as unknown as { state: RunState }).state = structuredClone(snap.state);
    // Schema is v:13. loadRun() in save.ts discards any payload with v !== 13 before
    // reaching this function. All Phase-15/16/19/21/23 fields (consumables, forcedNumber,
    // pendingInterestBonus, lastSpin, currentStreak, standardFichePurchasesThisVisit,
    // starting consumables, outside-sub boardLayout, pawnProgress, tetrisCompletedRows)
    // are guaranteed present in every v:12 snapshot.
    // Phase 22 (row-swap): dozen-sub cells are now at row 0; outside-sub cells at row 4.
    // The discard-on-mismatch policy (save.ts) is the single source of truth for
    // save validation — no forward-compat defaults are needed here.
    (gs as unknown as { ficheDeckObj: Deck<Fiche> }).ficheDeckObj = {
      draw: gs.getState().ficheDeck,
      discard: gs.getState().ficheDiscard,
    };
    (gs as unknown as { ballDeckObj: Deck<Ball> }).ballDeckObj = {
      draw: gs.getState().ballDeck,
      discard: gs.getState().ballDiscard,
    };
    // Advance the module-level counter past all fiche IDs that exist in this
    // save so shop purchases in this session never collide with saved fiches.
    // _instanceCounter resets to 0 on every page load but the save retains
    // inst-N ids from the original session — without this, the counter would
    // restart from 1 and immediately collide with existing inst-1, inst-2, …
    const allFiches = [
      ...gs.getState().ficheDeck,
      ...gs.getState().ficheDiscard,
      ...gs.getState().ficheHand,
    ];
    for (const f of allFiches) {
      const m = f.id.match(/^inst-(\d+)/);
      if (m) {
        const n = parseInt(m[1], 10);
        if (n > _instanceCounter) _instanceCounter = n;
      }
    }
    return gs;
  }

  private syncDecks(): void {
    this.state.ficheDeck = this.ficheDeckObj.draw;
    this.state.ficheDiscard = this.ficheDeckObj.discard;
    this.state.ballDeck = this.ballDeckObj.draw;
    this.state.ballDiscard = this.ballDeckObj.discard;
  }
}

export interface SaveSnapshot {
  v: 13;
  state: RunState;
}

// ------------------- deck construction -------------------

let _instanceCounter = 0;
function nextInstanceId(): string {
  return `inst-${++_instanceCounter}`;
}

export function buildStartingFicheDeck(): Fiche[] {
  // Phase 16 (FICHE-10): 2× standard. Third fiche is chosen in RunStartScene.
  const standardDef = FICHES_BY_ID['standard'];
  return Array.from({ length: 2 }, () => ({
    id: nextInstanceId(),
    ...standardDef,
  }));
}

export function buildStartingBallDeck(): Ball[] {
  // 4× standard_ball
  const standardDef = BALLS_BY_ID['standard_ball'];
  return Array.from({ length: 4 }, () => ({
    id: nextInstanceId(),
    ...standardDef,
  }));
}

export function ficheFromDef(defId: string): Fiche {
  const def = FICHES_BY_ID[defId];
  if (!def) throw new Error(`Unknown fiche defId: ${defId}`);
  return { id: nextInstanceId(), ...def };
}

export function ballFromDef(defId: string): Ball {
  const def = BALLS_BY_ID[defId];
  if (!def) throw new Error(`Unknown ball defId: ${defId}`);
  return { id: nextInstanceId(), ...def };
}

function defaultSeed(): string {
  return `roguelette-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
